import { TTicketDetails } from '@daymade/api-contracts';
import { PurchasingAction } from '@daymade/common-lib/dbEnums';
import BoostedBadge from 'components/common/BoostedBadge';
import EntryList from 'components/EntryList';
import {
  isLotteryGameType,
  isMainLotteryDraw,
} from 'components/how-it-works/GameSelector';
import RoundedButton from 'components/RoundedButton';
import { DaymadeDrawTypePlanSubcode } from 'constants/Draw';
import dayjs from 'dayjs';
import css from 'styled-jsx/css';
import {
  formattedPriceStringFromPence,
  formattedTokenCost,
  stringFromDate,
} from 'utils/common';
import { colors } from 'utils/constants';
import { getTicketFields } from './ticket/getTicketFields';
import { getTicketTypeText } from './ticket/getTicketTypeText';

interface IProps {
  ticket: TTicketDetails;
  gameId?: string;
  optionalRevealLink?: string;
  hidePurchaseDate?: boolean;
  hidePrice?: boolean;
}

const Ticket = ({
  ticket,
  optionalRevealLink,
  hidePurchaseDate = false,
  hidePrice = false,
}: IProps) => {
  const { ticketTypeText, showPurchaseDate } = getTicketTypeText(
    ticket.drawType.code,
    hidePurchaseDate,
    ticket.paymentRequest.purchasingAction
  );
  const numberOfEntries = ticket?.entries?.length;
  const { ticketType, ticketBanner } = getTicketFields(
    ticket.drawType.code,
    ticket.drawTypePlan.code,
    numberOfEntries
  );
  return (
    <div className="ticket">
      {ticketBanner && (
        <img
          src={ticketBanner}
          alt=""
          style={{
            height: 20,
            objectFit: 'cover',
          }}
        />
      )}

      <div className="ticketContent">
        {isMainLotteryDraw(ticket.drawType.code) && (
          <div
            className="flexRow flexSpaceBetween fullWidth"
            style={{ marginBottom: '0.5em' }}
          >
            <div className="flexLeftAligned">
              <h4>{ticketType}</h4>
              {!hidePrice && ticket.paymentRequest.tokenAmount === 0 ? (
                <p className="ticketPrice">
                  {formattedPriceStringFromPence(
                    ticket.paymentRequest.paymentMethodAmount
                  )}{' '}
                  {ticket.paymentRequest.discountAmount > 0 &&
                    `(incl. ${formattedPriceStringFromPence(ticket.paymentRequest.discountAmount)} discount)`}{' '}
                  {ticketTypeText}
                </p>
              ) : undefined}
              {ticket.paymentRequest.tokenAmount > 0 && (
                <div className="priceContainer">
                  <div className="ticketPrice">
                    {formattedTokenCost(ticket.paymentRequest.tokenAmount)}
                  </div>
                </div>
              )}
            </div>

            {ticket.drawTypePlan.subcode === DaymadeDrawTypePlanSubcode.BOOST && <BoostedBadge />}
            {ticket.drawTypePlan.subcode === DaymadeDrawTypePlanSubcode.BASE &&
              ticket.paymentRequest.purchasingAction ===
                PurchasingAction.PRIZE && (
                <p
                  style={{
                    background: '#04e4c2',
                    width: '70px',
                    fontSize: '0.75em',
                    height: '35px',
                  }}
                  className="rounded-full py-2 text-white flex items-center justify-center"
                >
                  FREE
                </p>
              )}
          </div>
        )}
        <div className="headerRow">
          <p>
            <strong>
              {isLotteryGameType(ticket.drawType.gameType) ? 'Draw' : 'Raffle'}{' '}
              date
            </strong>
          </p>

          <div className="flexRightAligned">
            <p style={{ margin: 0, fontWeight: 700 }}>
              {ticket.draw.drawDate
                ? stringFromDate(ticket.draw.drawDate)
                : 'TBD *'}
            </p>

            {isLotteryGameType(ticket.drawType.gameType) && (
              <p style={{ fontSize: '0.85em', fontStyle: 'italic' }}>
                {dayjs(ticket.draw.drawDate).format('dddd')}
              </p>
            )}
          </div>
        </div>
        {showPurchaseDate && (
          <>
            <div className="headerRow">
              <div className="purchaseDate">
                {ticket.paymentRequest.paymentMethodAmount > 0 ||
                ticket.paymentRequest.tokenAmount > 0
                  ? 'Purchase date'
                  : 'Date awarded'}
              </div>

              <div className="priceContainer">
                {stringFromDate(ticket.ticket.createdAt)}
              </div>
            </div>
          </>
        )}

        <div className="entryListContainer flexCentered">
          <EntryList
            ticket={ticket}
            pendingTicket={undefined}
            editable={undefined}
            withPreviousEntriesLink={undefined}
            onEdit={undefined}
            numberOfEntriesIsFixed={undefined}
          />
        </div>
        {optionalRevealLink && (
          <RoundedButton
            href={optionalRevealLink}
            style={{ margin: '1em 2em 2em' }}
          >
            Watch Reveal
          </RoundedButton>
        )}
        {!ticket.draw.drawDate && (
          <p style={{ fontStyle: 'italic', fontSize: '0.8em' }}>
            * The winning entry will be drawn as soon as all entries have been
            claimed
          </p>
        )}
      </div>

      <style jsx>{styles}</style>
    </div>
  );
};

const styles = css`
  .ticket {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    max-width: 500px;
    background-color: white;
    box-shadow:
      0 0px 1px rgba(0, 0, 0, 0.12),
      0 0px 2px rgba(0, 0, 0, 0.12),
      0 0px 4px rgba(0, 0, 0, 0.12),
      0 8px 8px rgba(0, 0, 0, 0.12),
      0 16px 16px rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    z-index: 0;
    overflow: hidden;
    font-size: 1rem;
  }

  .priceContainer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
  }

  .ticketPrice {
    font-weight: 700;
    color: ${colors.primaryBrandGray}99;
    font-size: 0.9em;
  }

  .ticketContent {
    display: flex;
    flex-direction: column;
    padding: 2em 1.25em;
  }

  .headerRow {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    border-bottom: 3px solid #eee;
    font-weight: bold;
  }

  :global(.faIcon) {
    margin: 5px 10px;
  }

  .purchaseDate,
  .prizeDrawDate {
    display: flex;
    flex-direction: column;
    font-weight: 800;
  }

  .entryListContainer {
    margin: 1em 0 0;
  }

  h4 {
    letter-spacing: 0;
    text-transform: none;
    font-size: 1.3em;
    margin: 0;
    display: inline-block;
  }

  p {
    margin: 0;
  }

  .boostedBadge {
    background: #ff399e;
    border-radius: 30px;
    color: white;
    font-size: 0.75em;
    font-weight: 700;
    padding: 0.9em 1.1em;
    text-transform: uppercase;
    margin-left: 1.25em;
  }

  .boostedBadge img {
    width: 13px;
    margin-right: 0.5em;
  }

  @media (min-width: 450px) {
    .ticketContent {
      padding: 2em 1.5em 0.5em;
    }
  }
`;

export default Ticket;
